import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import React, { useState } from "react";
import { ToastContainer } from "react-toastify";
import { useLoadingToast } from "../../../hooks/toast/toasts";
import {
  useDocumentTypes,
  useToggleDocumentType,
  useDeleteDocumentType,
} from "../../../hooks/api/documentTypesApiHooks";
import DocumentTypeFormModal from "./DocumentTypeFormModal";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";

/**
 * Componente de gestão de tipos de documentos (Admin)
 */
export default function DocumentTypesManagement() {
  // Estados
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Hooks de API e Toast
  const { data: documentTypes, isLoading, error, refetch } = useDocumentTypes(false);
  const { mutateAsync: toggleType } = useToggleDocumentType();
  const { mutateAsync: deleteType } = useDeleteDocumentType();
  const { initializeLoadingToast, updateLoadingToast } = useLoadingToast();

  // Handlers
  const handleCreate = () => {
    setSelectedDocumentType(null);
    setIsEditing(false);
    setFormModalOpen(true);
  };

  const handleEdit = (documentType) => {
    setSelectedDocumentType(documentType);
    setIsEditing(true);
    setFormModalOpen(true);
  };

  const handleDelete = (documentType) => {
    setSelectedDocumentType(documentType);
    setDeleteDialogOpen(true);
  };

  const handleToggle = async (documentType) => {
    let toastId;
    try {
      toastId = initializeLoadingToast({
        text: `${documentType.isActive ? "Desativando" : "Ativando"} tipo...`,
      });

      await toggleType({
        id: documentType.id,
        isActive: !documentType.isActive,
      });

      updateLoadingToast({
        id: toastId,
        text: `Tipo ${documentType.isActive ? "desativado" : "ativado"} com sucesso!`,
        type: "success",
      });

      refetch();
    } catch (error) {
      console.error("Erro ao alternar tipo:", error);
      const errorMessage =
        error.response?.data?.message || "Erro ao alternar status do tipo";
      updateLoadingToast({
        id: toastId,
        text: errorMessage,
        type: "error",
      });
    }
  };

  const handleConfirmDelete = async () => {
    let toastId;
    try {
      toastId = initializeLoadingToast({
        text: "Deletando tipo de documento...",
      });

      await deleteType(selectedDocumentType.id);

      updateLoadingToast({
        id: toastId,
        text: "Tipo deletado com sucesso!",
        type: "success",
      });

      setDeleteDialogOpen(false);
      setSelectedDocumentType(null);
      refetch();
    } catch (error) {
      console.error("Erro ao deletar tipo:", error);
      const errorMessage =
        error.response?.data?.message || "Erro ao deletar tipo de documento";
      updateLoadingToast({
        id: toastId,
        text: errorMessage,
        type: "error",
      });
    }
  };

  const handleFormSuccess = () => {
    setFormModalOpen(false);
    setSelectedDocumentType(null);
    refetch();
  };

  // Ordenar por displayOrder
  const sortedTypes = [...(documentTypes || [])].sort(
    (a, b) => a.displayOrder - b.displayOrder
  );

  return (
    <Box>
      <ToastContainer />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          📄 Gestão de Tipos de Documentos
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Novo Tipo
        </Button>
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Erro ao carregar tipos de documentos: {error.message}
        </Alert>
      )}

      {/* Table */}
      {!isLoading && !error && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.100" }}>
                <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Categoria</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Pasta OneDrive</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Extensões</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tamanho Máx</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTypes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      Nenhum tipo de documento cadastrado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedTypes.map((type) => (
                  <TableRow
                    key={type.id}
                    hover
                    sx={{
                      opacity: type.isActive ? 1 : 0.6,
                      bgcolor: type.isActive ? "transparent" : "grey.50",
                    }}
                  >
                    <TableCell>{type.displayOrder}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {type.name}
                      </Typography>
                      {type.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {type.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={type.category}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                        📁 {type.folderPath || "Documentos"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {type.allowedExtensions.join(", ").toUpperCase()}
                      </Typography>
                    </TableCell>
                    <TableCell>{type.maxSizeMB} MB</TableCell>
                    <TableCell>
                      <Chip
                        label={type.isActive ? "Ativo" : "Inativo"}
                        size="small"
                        color={type.isActive ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                        <Tooltip
                          title={type.isActive ? "Desativar" : "Ativar"}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleToggle(type)}
                            color={type.isActive ? "success" : "default"}
                          >
                            {type.isActive ? (
                              <ToggleOnIcon />
                            ) : (
                              <ToggleOffIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(type)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deletar">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(type)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modais */}
      {formModalOpen && (
        <DocumentTypeFormModal
          isOpen={formModalOpen}
          onClose={() => setFormModalOpen(false)}
          documentType={selectedDocumentType}
          isEditing={isEditing}
          onSuccess={handleFormSuccess}
          initializeLoadingToast={initializeLoadingToast}
          updateLoadingToast={updateLoadingToast}
        />
      )}

      {deleteDialogOpen && (
        <DeleteConfirmationDialog
          isOpen={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          documentType={selectedDocumentType}
          onConfirm={handleConfirmDelete}
        />
      )}
    </Box>
  );
}

